import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import Cancellation from "./Cancellation";

import { AppState } from "../../../../store";
import { cancelDispensation as cancel } from "../../../../store/prescription/actions";

import { PrescriptionItemInterface } from "../../../../interfaces/prescription";
import { PermissionsInterface } from "../../../../interfaces/drugstore";
import ItemsStatusInterface from "../../../../interfaces/itemsStatus";
import { useToast } from "../../../ui/Toast";

interface StateProps {
  token: string;
  itemsProps: PrescriptionItemInterface[];
  mustUpdateHistory: boolean;
  permissions: PermissionsInterface;
  itemsStatus: ItemsStatusInterface[];
}

interface DispatchProps {
  cancelDispensation: Function;
}

type Props = StateProps & DispatchProps;

const CancellationContainer: React.FC<Props> = ({
  token,
  itemsProps,
  mustUpdateHistory,
  permissions,
  itemsStatus,
  cancelDispensation,
}: Props) => {
  const { addToast } = useToast();

  const [loadingItem, isLoadingItem] = useState<number | boolean>(false);
  const [items, setItems] = useState<PrescriptionItemInterface[]>(itemsProps);

  const filterItems = useCallback(
    (itemsList: PrescriptionItemInterface[]): PrescriptionItemInterface[] => {
      return itemsList.filter(
        (item: PrescriptionItemInterface) => item.dispensation.data.length,
      );
    },
    [],
  );

  const handleCancel = async (id: number): Promise<void> => {
    isLoadingItem(id);
    try {
      await cancelDispensation(id, token)
    } catch (error: any) {
      if (error.response?.data.errors?.dispensation_ids) {
        addToast({
          title: "Erro ao cancelar dispensação",
          description: error.response?.data.errors.dispensation_ids,
          status: "danger",
        });
        return
      }
      addToast({
        title: "Erro ao cancelar dispensação",
        description: "Erro ao cancelar dispensação",
        status: "danger",
      });
    } finally {
      isLoadingItem(false)
    }
  };

  useEffect(() => {
    if (!mustUpdateHistory) {
      return;
    }

    const filteredItems = filterItems(itemsProps);

    // Agrupar itens por medicamento
    const grouped = new Map<number, PrescriptionItemInterface>();
    filteredItems.forEach((item) => {
      if (grouped.has(item.id)) {
        // Se já existe, consolidar as dispensações
        const existingItem = grouped.get(item.id);
        if (existingItem) {
          existingItem.dispensation.data = [
            ...existingItem.dispensation.data,
            ...item.dispensation.data,
          ];
        }
      } else {
        // Se não existe, adicionar o item completo
        grouped.set(item.id, { ...item });
      }
    });
    const groupedItems = Array.from(grouped.values());

    setItems(groupedItems);
  }, [itemsProps, mustUpdateHistory, filterItems]);

  return (
    <Cancellation
      items={items}
      cancelDispensation={handleCancel}
      loadingItem={loadingItem}
      permissions={permissions}
      itemsStatus={itemsStatus}
    />
  );
};

const mapStateToProps = ({
  prescription,
  permissions,
}: AppState): StateProps => ({
  token: prescription.prescription.token,
  itemsProps: prescription.prescription.items,
  mustUpdateHistory: prescription.mustUpdateHistory,
  permissions,
  itemsStatus: prescription.itemsStatus,
});

const mapDispatchToProps = {
  cancelDispensation: cancel,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CancellationContainer);
